import Layout from '@/components/feature/Layout';
import SEOHead from '@/components/base/SEOHead';
import { localBusinessSchema, buildBreadcrumbSchema, buildGraphSchema } from '@/components/base/schemas';
import { Link } from 'react-router-dom';

const breadcrumbSchema = buildBreadcrumbSchema([
  { name: 'Accueil', item: 'https://fenetre-paris15.fr/' },
  { name: 'Qui sommes-nous', item: 'https://fenetre-paris15.fr/qui-sommes-nous/' },
]);

export default function QuiSommesNousPage() {
  return (
    <Layout>
      <SEOHead
        title="DF PRO — Réparateur de Fenêtres à Paris 15 | Société Assurée"
        description="DF PRO, spécialiste de la réparation de fenêtres et volets dans le 15ᵉ. Techniciens salariés, société assurée, plus de 10 ans d'expérience."
        canonicalUrl="https://fenetre-paris15.fr/qui-sommes-nous/"
        schema={buildGraphSchema(localBusinessSchema, breadcrumbSchema)}
      />

      <div className="bg-brand-mint/30 py-3">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <nav aria-label="Fil d'Ariane" className="text-xs text-brand-text/60">
            <Link to="/" className="hover:text-brand-accent">Accueil</Link><span className="mx-2">/</span><span className="text-brand-accent font-medium">Qui sommes-nous</span>
          </nav>
        </div>
      </div>

      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-brand-dark mb-6">DF PRO, votre réparateur de fenêtres à Paris 15ᵉ</h1>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10 mb-12">
            <div>
              <p className="text-brand-text/70 leading-relaxed mb-4">
                DF PRO est une <strong>entreprise artisanale indépendante</strong> spécialisée dans la réparation et l'entretien de fenêtres et menuiseries. Nous sommes implantés au cœur du 15ᵉ arrondissement de Paris et intervenons exclusivement sur ce territoire que nous connaissons parfaitement.
              </p>
              <p className="text-brand-text/70 leading-relaxed mb-4">
                Notre métier, c'est la <strong>quincaillerie de précision</strong>. Poignées, crémones, gâches, paumelles, galets, sangles, moteurs de volets, joints : nous diagnostiquons et réparons les mécanismes de vos fenêtres, sans remplacer inutilement l'ouvrant. Une approche à la fois économique et écologique.
              </p>
            </div>
            <div className="aspect-[4/3] rounded-2xl overflow-hidden">
              <img src="/images/dfpro-about.webp" alt="Atelier DF PRO — artisan spécialiste quincaillerie fenêtres Paris 15" className="w-full h-full object-cover" loading="lazy" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 mb-12">
            {[
              { icon: 'ri-team-line', title: 'Techniciens salariés', desc: `Contrairement à beaucoup d'entreprises du secteur, nos techniciens sont salariés, pas sous-traitants. Ils sont formés en interne aux trois matériaux (PVC, aluminium, bois) et partagent nos valeurs de qualité.` },
              { icon: 'ri-shield-check-line', title: 'Société assurée RC Pro', desc: `DF PRO est couvert par une assurance responsabilité civile professionnelle. Votre logement et vos biens sont protégés pendant toute la durée de l'intervention.` },
              { icon: 'ri-award-line', title: `Plus de 10 ans d'expérience`, desc: `Notre savoir-faire s'est forgé sur des milliers d'interventions dans le 15ᵉ. Chaque quartier, chaque type de bâti, chaque marque : nous maîtrisons.` },
            ].map((item, idx) => (
              <div key={idx} className="bg-brand-mint/30 rounded-xl p-5 md:p-6 text-center">
                <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-brand-mint text-brand-accent flex items-center justify-center">
                  <i className={`${item.icon} text-xl`}></i>
                </div>
                <h3 className="font-heading font-bold text-brand-dark mb-2">{item.title}</h3>
                <p className="text-brand-text/60 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="bg-gradient-to-br from-brand-gradientFrom to-brand-gradientTo rounded-2xl p-8 md:p-10 text-white">
            <h2 className="font-heading text-2xl font-bold mb-4">Notre engagement qualité</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm text-white/80">
              <div className="flex items-start gap-3">
                <i className="ri-check-line text-white mt-0.5"></i>
                <span><strong className="text-white">Diagnostic précis :</strong> nous identifions la panne avant d'agir.</span>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-check-line text-white mt-0.5"></i>
                <span><strong className="text-white">Devis gratuit :</strong> vous savez exactement ce que vous allez payer.</span>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-check-line text-white mt-0.5"></i>
                <span><strong className="text-white">Sans engagement :</strong> vous restez libre d'accepter ou non.</span>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-check-line text-white mt-0.5"></i>
                <span><strong className="text-white">Intervention déclarée :</strong> facture, garantie, traçabilité.</span>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-check-line text-white mt-0.5"></i>
                <span><strong className="text-white">Réparation, pas remplacement :</strong> nous privilégions toujours la solution la moins invasive.</span>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-check-line text-white mt-0.5"></i>
                <span><strong className="text-white">SIRET :</strong> entreprise enregistrée, intervention en règle.</span>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link to="/devis-fenetre-paris-15/" className="btn-primary text-sm">
              <i className="ri-file-text-line"></i> Demander un devis gratuit
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}